/**
 * AURR (Autodesk University React Render) acts as a bridge
 * between AU's Gutenberg Blocks and the React Rendering engine
 *
 * Author: Relevant Bits
 */

// import dependencies
import React, { useState, useEffect } from 'react';

import { locale as localeConfig } from '@adsk/au-common-js';
import ReactDOM from 'react-dom';
import { createRoot } from 'react-dom/client';

import { generateConfig } from './config';
import { dispatchReadyEvent, dispatchRenderEvent } from './events';
import library from './library';
import { getDatasetValueFromScript } from './utils';
import { AURRProvider } from './with-aurr-context';

/**
 * Query document for list of AURR components
 * @returns {object[]} array of component definitions
 */
const queryComponentKeys = () => {
  const elements = document.getElementsByClassName('aurrComponent');
  let nextID = Date.now();

  const components = Array.from(elements).map(element => {
    /* eslint-disable-next-line no-param-reassign */
    if (!element.id) element.id = `aurr-${(nextID += 1)}`;
    return element.id;
  });
  return components;
};

/**
 * Creates a ReactComponent from an element
 * @param {*} key The element id defining the react component
 * @returns {React.Component} The generated component
 */
const elementToComponent = key => {
  const element = document.getElementById(key);

  const Component = library[element.dataset.component];
  if (Component) {
    const props = { key };

    if (Component.attributes) {
      Object.keys(Component.attributes).forEach(propName => {
        const propValue =
          element.dataset[propName.toString()] ||
          element.dataset[propName.toLowerCase()];
        if (propValue) {
          switch (Component.attributes[propName].type) {
            case 'boolean':
              props[propName.toString()] = propValue === 'true';
              break;
            case 'object':
            case 'array':
              props[propName.toString()] = JSON.parse(propValue);
              break;
            case 'integer':
              props[propName.toString()] = +propValue;
              break;
            default:
              props[propName.toString()] = propValue.toString();
          }
        }
      });
    }

    const component = React.createElement(Component, props);
    dispatchRenderEvent({ element });
    return ReactDOM.createPortal(component, element, element.id);
  }
  return null;
};

/**
 * The root React component
 * @param {Object} params Component parameters
 * @returns {React.Component} The main React App component
 */
function Aurr() {
  const [keys, setKeys] = useState(queryComponentKeys());

  /**
   * load new components on changes to page
   * @param {*} e event parameters
   * @returns {undefined}
   */
  const handlePageUpdate = () => {
    setKeys(queryComponentKeys());
  };

  // add/remove event listeners on component mount/unmount
  useEffect(() => {
    window.addEventListener('aurrPageUpdate', handlePageUpdate);
    dispatchReadyEvent();
    return () => {
      window.removeEventListener('aurrPageUpdate', handlePageUpdate);
    };
  }, []);

  // Gather marketing parameters that AURR will need to append to links it
  // outputs. We rely on configuration and logic provided by AUO
  // Analytics.
  // @see drupal/src/modules/custom/auo_analytics
  // @see drupal/src/modules/custom/auo_analytics/src/Form/DataAnalyticsSettings.php
  const marketingParameters = [];

  window.Drupal = window.Drupal || {};
  window.Drupal.auoAnalyticsRetainMarketingParameters =
    window.Drupal.auoAnalyticsRetainMarketingParameters || {};

  window.drupalSettings = window.drupalSettings || {};
  window.drupalSettings.auoAnalyticsRetainMarketingParameters =
    window.drupalSettings.auoAnalyticsRetainMarketingParameters || {};
  window.drupalSettings.aurr_auth_config =
    window.drupalSettings.aurr_auth_config || {};
  window.drupalSettings.aurr_environment =
    window.drupalSettings.aurr_environment || {};
  window.drupalSettings.path = window.drupalSettings.path || {};

  const {
    drupalSettings: {
      auoAnalyticsRetainMarketingParameters: marketingParamsConfig,
      aurr_auth_config,
      aurr_environment,
      path: { currentLanguage },
    },
    Drupal: {
      auoAnalyticsRetainMarketingParameters: { getParameterByName },
    },
  } = window;

  if (
    window.location.search &&
    marketingParamsConfig &&
    marketingParamsConfig.status &&
    marketingParamsConfig.parameters &&
    marketingParamsConfig.parameters.length &&
    getParameterByName
  ) {
    marketingParamsConfig.parameters.forEach(param => {
      const paramValue = getParameterByName(param);
      if (paramValue !== null) {
        marketingParameters.push({
          name: param,
          value: paramValue,
        });
      }
    });
  }

  let configOverides = {};
  if (typeof aurr_environment === 'string' && aurr_environment.length > 0) {
    configOverides = generateConfig(aurr_environment);
  }

  const authOverrides = {};
  if (aurr_auth_config.redirect_url) {
    authOverrides.LOGIN_REDIRECT_URL = aurr_auth_config.redirect_url;
  }
  if (aurr_auth_config.client_id) {
    authOverrides.LOGIN_CLIENT_ID = aurr_auth_config.client_id;
  }
  if (aurr_auth_config.env) {
    authOverrides.LOGIN_CLIENT_ENV = aurr_auth_config.env;
  }
  if (aurr_auth_config.sign_in_link) {
    authOverrides.LOGIN_SIGN_IN_LINK = `${
      aurr_auth_config.sign_in_link
    }?redirect=${window.location.origin + window.location.pathname}`;
  }
  if (aurr_auth_config.sign_out_link) {
    authOverrides.LOGIN_SIGN_OUT_LINK = `${
      aurr_auth_config.sign_out_link
    }?redirect=${window.location.origin + window.location.pathname}`;
  }

  let language;
  if (currentLanguage) {
    language = currentLanguage;
  } else {
    language = getDatasetValueFromScript(
      'language',
      localeConfig.defaultURLLocale
    );
  }

  return (
    <AURRProvider
      language={language}
      marketingParameters={marketingParameters}
      appConfig={{
        ...configOverides,
        ...authOverrides,
      }}
    >
      {keys ? Array.from(keys, elementToComponent) : null}
    </AURRProvider>
  );
}

// create react document root if needed
const elRootId = 'aurr';
let domElRoot = document.getElementById(elRootId);
if (domElRoot === null) {
  domElRoot = document.createElement('div');
  domElRoot.id = elRootId;
  document.body.appendChild(domElRoot);
}

// render the main app
const root = createRoot(domElRoot);
root.render(<Aurr />); // Use the root to render.
